// A single draggable product card on the catalog board. The drag handle is a
// dedicated button (so the card body stays clickable to open the editor and so
// keyboard users can pick the card up explicitly). Inactive products are dimmed
// and badged rather than hidden, so admins can find and reactivate them.
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box, Paper, Typography, Chip, IconButton,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { materialSymbolSx } from '../../../theme.js';

const ProductCard = ({ product, onEdit }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: `product-${product.id}`, data: { type: 'product', product } });

  // The floating preview is rendered by the board's DragOverlay. This in-place
  // card keeps the normal sortable transform/transition so verticalListSorting
  // Strategy animates the other cards aside, and its slot stays reserved (just
  // dimmed) — that reserved slot is what prevents gaps from opening up. We must
  // NOT also reorder the array on same-column hover; the strategy owns that, and
  // doing both double-shifts every card.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        opacity: product.is_active ? 1 : 0.55,
      }}
    >
      <IconButton
        size="small"
        aria-label={`Reorder ${product.name}`}
        sx={{ cursor: 'grab', touchAction: 'none' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      {product.icon_key && (
        <Box component="span" aria-hidden="true" sx={{ ...materialSymbolSx, fontSize: 22, color: 'text.secondary' }}>
          {product.icon_key}
        </Box>
      )}

      <Box
        component="button"
        type="button"
        onClick={() => onEdit(product)}
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          border: 0,
          bgcolor: 'transparent',
          cursor: 'pointer',
          p: 0,
          color: 'text.primary',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', borderRadius: 1 },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
          {product.name}
        </Typography>
        {product.short_description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            {product.short_description}
          </Typography>
        )}
      </Box>

      {!product.is_active && <Chip label="Inactive" size="small" />}
    </Paper>
  );
};

// Floating preview rendered inside the board's DragOverlay while a card is
// dragged. Presentational only (no useSortable), so it can mirror the resting
// card without re-registering the active id.
export const ProductCardPreview = ({ product }) => (
  <Paper
    variant="outlined"
    elevation={6}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      p: 1,
      borderLeft: 3,
      borderColor: 'primary.main',
      bgcolor: 'background.paper',
      cursor: 'grabbing',
      boxShadow: 6,
    }}
  >
    <DragIndicatorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
    {product.icon_key && (
      <Box component="span" aria-hidden="true" sx={{ ...materialSymbolSx, fontSize: 22, color: 'text.secondary' }}>
        {product.icon_key}
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
        {product.name}
      </Typography>
      {product.short_description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
          {product.short_description}
        </Typography>
      )}
    </Box>
    {!product.is_active && <Chip label="Inactive" size="small" />}
  </Paper>
);

export default ProductCard;
